import { RecurrenceDefinition } from 'src/app/core/models/schedule.models';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  DialogCloseOptions,
  DialogCloseReason,
  injectDirtyCloseHandler,
  SuiDialogSimpleComponent,
  SuiDialogHeaderSlot,
  SuiDialogContentSlot,
  SuiDialogFooterSlot,
  SuiDialogConfig,
} from '@sui/dialog';
import { skip } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SimpleDialogBase } from '../../abstract/simple-dialog-base';
import { NomenclatureService } from 'src/app/core/services/nomenclature.service';
import { NomenclatureItem } from 'src/app/core/models/core.models';
import { SuiButtonComponent } from '@sui/button';
import {
  SuiButtonGroupComponent,
  SuiButtonGroupOption,
} from '@sui/button-group';
import { NgpLabel } from 'ng-primitives/form-field';
import { SuiLabelDirective } from '@sui/form-field';
import { SuiInputComponent } from '@sui/input';
import { SuiSliderComponent } from '@sui/slider';
import { SuiCheckboxComponent } from '@sui/checkbox';
import { SuiSelectComponent, SuiSelectOptionComponent } from '@sui/select';
import { AlgorithmScheduleComponent } from '../algorithm-schedule/algorithm-schedule.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

export type RelativePeriodType = 'hour' | 'day' | 'week';

export type LinkRelatedArticlesProminence = 'source_rank' | 'potential_reach';

export const LINK_RELATED_ARTICLES_PROMINENCE_OPTIONS: ReadonlyArray<{
  id: LinkRelatedArticlesProminence;
  labelKey: string;
}> = [
  {
    id: 'source_rank',
    labelKey: 'link-related-articles.prominence-option.source-rank',
  },
  {
    id: 'potential_reach',
    labelKey: 'link-related-articles.prominence-option.potential-reach',
  },
];

export const RELATIVE_PERIOD_TYPES: RelativePeriodType[] = [
  'hour',
  'day',
  'week',
];

export const RELATIVE_AMOUNT_MIN = 1;
export const RELATIVE_AMOUNT_MAX = 30;

interface LinkRelatedArticlesDialogInput {
  // Stored algorithm params carry `feed_id` instead of `current_feed_only`:
  // the save path (post-algorithm.service paramsProvider) persists
  // `feed_id: current_feed_only ? feedId : null`.
  initialPayload?: Partial<LinkRelatedArticlesPayload> & {
    feed_id?: string | null;
  };
  canSchedule?: boolean;
  editMode?: boolean;
}

export interface LinkRelatedArticlesPayload {
  min_similarity: number;
  max_similarity: number;
  results: number;
  current_feed_only: boolean;
  period_type: RelativePeriodType;
  relative_amount: number;
  prominence: LinkRelatedArticlesProminence;
  language_id: number | null;
  schedule?: boolean;
  schedule_interval_minutes?: number;
  schedule_recurrence?: RecurrenceDefinition;
  schedule_timezone_id?: number | null;
}

interface LinkRelatedArticlesDialogOutput {
  payload: LinkRelatedArticlesPayload;
}

const SIMILARITY_RANGE_VALIDATOR: ValidatorFn = (
  group: AbstractControl,
): ValidationErrors | null => {
  const min = group.get('min_similarity')?.value;
  const max = group.get('max_similarity')?.value;
  if (min == null || max == null) return null;
  return Number(min) <= Number(max) ? null : { similarityRange: true };
};

@Component({
  selector: 'app-link-related-articles-dialog',
  templateUrl: './link-related-articles-dialog.component.html',
  styleUrl: './link-related-articles-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SuiDialogSimpleComponent,
    SuiDialogHeaderSlot,
    SuiButtonComponent,
    SuiDialogContentSlot,
    FormsModule,
    ReactiveFormsModule,
    NgpLabel,
    SuiLabelDirective,
    SuiInputComponent,
    SuiSliderComponent,
    SuiButtonGroupComponent,
    SuiCheckboxComponent,
    SuiSelectComponent,
    SuiSelectOptionComponent,
    AlgorithmScheduleComponent,
    SuiDialogFooterSlot,
    TranslatePipe,
  ],
})
export class LinkRelatedArticlesDialogComponent
  extends SimpleDialogBase<
    LinkRelatedArticlesDialogInput,
    LinkRelatedArticlesDialogOutput
  >
  implements OnInit, AfterViewInit
{
  static dialogConfig: Partial<SuiDialogConfig> = { width: 480 };

  protected override dirtyHandler = injectDirtyCloseHandler(this.dialogRef);

  form: FormGroup;

  protected isDirty = signal(false);
  protected isValid = signal(false);
  protected scheduleMode = signal(false);

  private readonly translate = inject(TranslateService);
  // The period type segments, in the order of RELATIVE_PERIOD_TYPES.
  protected readonly periodTypeOptions: SuiButtonGroupOption[] =
    RELATIVE_PERIOD_TYPES.map((type) => ({
      label: this.translate.instant(this.periodTypeLabelKey(type)),
      value: type,
    }));
  protected readonly amountMin = RELATIVE_AMOUNT_MIN;
  protected readonly amountMax = RELATIVE_AMOUNT_MAX;
  protected readonly prominenceOptions =
    LINK_RELATED_ARTICLES_PROMINENCE_OPTIONS;
  protected languageOptions: NomenclatureItem[] = [];

  protected runButtonText = computed(() => {
    if (this.editMode) {
      return this.translateService.instant('btn.save');
    }
    if (this.canSchedule && this.scheduleMode()) {
      return this.translateService.instant('btn.schedule');
    }
    if (this.canSchedule) {
      return this.translateService.instant('btn.run-now');
    }
    return this.translateService.instant(
      'link-related-articles.form-button.run',
    );
  });

  protected runButtonIcon = computed<IconProp | undefined>(() => {
    if (this.editMode) return undefined;
    if (!this.canSchedule) return undefined;
    return this.scheduleMode() ? ['fas', 'clock'] : ['fas', 'play'];
  });

  private destroyRef = inject(DestroyRef);
  private formBuilder = inject(FormBuilder);
  private nomenclatureService = inject(NomenclatureService);

  get canSchedule(): boolean {
    return !!this.dialogData?.canSchedule;
  }
  get editMode(): boolean {
    return !!this.dialogData?.editMode;
  }
  get showSchedule(): boolean {
    return this.canSchedule || this.editMode;
  }
  get initialIntervalMinutes(): number {
    const value = this.dialogData?.initialPayload?.schedule_interval_minutes;
    return typeof value === 'number' && value > 0 ? value : 60;
  }
  get initialRecurrence() {
    return (
      (this.dialogData?.initialPayload as any)?.['schedule_recurrence'] ?? null
    );
  }
  get initialTimezoneId() {
    return (
      (this.dialogData?.initialPayload as any)?.['schedule_timezone_id'] ?? null
    );
  }

  ngOnInit(): void {
    this.initializeForm();
    this.applyInitialPayload();
    this.setupFormChangeTracking();
    this.loadLanguages();
    this.isValid.set(this.form.valid);
  }

  private loadLanguages(): void {
    this.nomenclatureService
      .fetchLanguages(false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((languages) => {
        this.languageOptions = [...languages].sort((a, b) =>
          (a.name ?? a.title ?? '').localeCompare(b.name ?? b.title ?? ''),
        );
      });
  }

  ngAfterViewInit(): void {
    this.setupDirtyHandler(this.isDirty, this.isValid);
  }

  private initializeForm(): void {
    this.form = this.formBuilder.group(
      {
        min_similarity: [
          90,
          [Validators.required, Validators.min(1), Validators.max(100)],
        ],
        max_similarity: [
          95,
          [Validators.required, Validators.min(1), Validators.max(100)],
        ],
        results: [
          10,
          [Validators.required, Validators.min(1), Validators.max(10)],
        ],
        current_feed_only: [false],
        period_type: ['day' as RelativePeriodType, Validators.required],
        relative_amount: [
          7,
          [
            Validators.required,
            Validators.min(RELATIVE_AMOUNT_MIN),
            Validators.max(RELATIVE_AMOUNT_MAX),
          ],
        ],
        prominence: [
          'source_rank' as LinkRelatedArticlesProminence,
          Validators.required,
        ],
        language_id: [null as number | null],
      },
      { validators: [SIMILARITY_RANGE_VALIDATOR] },
    );
  }

  private applyInitialPayload(): void {
    const initial = this.dialogData?.initialPayload;
    if (!initial) return;

    const patch: Partial<LinkRelatedArticlesPayload> = {};
    if (typeof initial.min_similarity === 'number') {
      patch.min_similarity =
        initial.min_similarity <= 1
          ? Math.round(initial.min_similarity * 100)
          : initial.min_similarity;
    }
    if (typeof initial.max_similarity === 'number') {
      patch.max_similarity =
        initial.max_similarity <= 1
          ? Math.round(initial.max_similarity * 100)
          : initial.max_similarity;
    }
    if (typeof initial.results === 'number') {
      patch.results = initial.results;
    }
    if (typeof initial.current_feed_only === 'boolean') {
      patch.current_feed_only = initial.current_feed_only;
    } else if (initial.feed_id !== undefined) {
      // Reverse-map the persisted shape (WAP-1510): a non-empty feed_id means
      // "Current feed only" was selected when the params were saved.
      patch.current_feed_only =
        initial.feed_id !== null && initial.feed_id !== '';
    }
    if (
      typeof initial.period_type === 'string' &&
      RELATIVE_PERIOD_TYPES.includes(initial.period_type)
    ) {
      patch.period_type = initial.period_type;
    }
    if (typeof initial.relative_amount === 'number') {
      patch.relative_amount = Math.min(
        RELATIVE_AMOUNT_MAX,
        Math.max(RELATIVE_AMOUNT_MIN, Math.round(initial.relative_amount)),
      );
    }
    if (
      initial.prominence === 'source_rank' ||
      initial.prominence === 'potential_reach'
    ) {
      patch.prominence = initial.prominence;
    }
    if (initial.language_id === null) {
      patch.language_id = null;
    } else if (typeof initial.language_id === 'number') {
      patch.language_id = initial.language_id;
    }
    this.form.patchValue(patch);
  }

  private setupFormChangeTracking(): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), skip(1))
      .subscribe(() => {
        this.isDirty.set(true);
        this.isValid.set(this.form.valid);
        this.scheduleMode.set(!!this.form.get('schedule')?.value);
      });
  }

  hasSimilarityRangeError(): boolean {
    return (
      this.form?.errors?.['similarityRange'] &&
      (this.form.get('min_similarity')?.touched ||
        this.form.get('max_similarity')?.touched)
    );
  }

  protected periodTypeLabelKey(type: RelativePeriodType): string {
    return `link-related-articles.form.period-type.${type}`;
  }

  async onCancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }

  onSubmit(): void {
    if (!this.form.valid) return;
    const payload = this.buildPayload();
    this.dirtyHandler.closeImmediately({
      reason: DialogCloseReason.Save,
      data: { payload },
    });
  }

  protected override performSave(): DialogCloseOptions<LinkRelatedArticlesDialogOutput> {
    const payload = this.buildPayload();
    return {
      reason: DialogCloseReason.Save,
      data: { payload },
    };
  }

  private buildPayload(): LinkRelatedArticlesPayload {
    const value = this.form.value;
    const languageId =
      value.language_id === null || value.language_id === ''
        ? null
        : Number(value.language_id);
    const payload: LinkRelatedArticlesPayload = {
      min_similarity: Number(value.min_similarity) / 100,
      max_similarity: Number(value.max_similarity) / 100,
      results: Number(value.results),
      current_feed_only: !!value.current_feed_only,
      period_type: value.period_type as RelativePeriodType,
      relative_amount: Number(value.relative_amount),
      prominence: value.prominence as LinkRelatedArticlesProminence,
      language_id: languageId,
      schedule: !!value.schedule,
    };

    if (value.schedule) {
      payload.schedule_recurrence = value.schedule_recurrence;
      payload.schedule_timezone_id = value.schedule_timezone_id;
    }

    return payload;
  }
}
